import { baseApi } from '../../../core/baseApi';
import type { Schema } from '@voley/shared-types';
import {
  NotificationFeedResponse,
  UnreadCountResponse,
  NotificationPreferences,
  AuditLogResponse,
  AuditLogQueryParams
} from '../types';

export const notificationsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getNotificationFeed: builder.query<NotificationFeedResponse, { page?: number; limit?: number }>({
      query: ({ page = 1, limit = 10 } = {}) => `/notifications?page=${page}&limit=${limit}`,
      providesTags: ['Notifications'],
      // High-volatility endpoint: refetch if data is older than 5 minutes on mount
      // Notifications are time-sensitive and should stay current
      // @ts-expect-error - RTK Query supports this at endpoint level but types don't include it
      refetchOnMountOrArgChange: 5 * 60,
      transformResponse: (response: Schema<'NotificationFeedEnvelopeResponse'>) => response.data,
    }),
    getNotificationUnreadCount: builder.query<UnreadCountResponse, void>({
      query: () => '/notifications/unread-count',
      providesTags: ['NotificationCount'],
      // High-volatility endpoint: refetch if data is older than 5 minutes on mount
      // Unread count badge should reflect current state after returning to app
      // @ts-expect-error - RTK Query supports this at endpoint level but types don't include it
      refetchOnMountOrArgChange: 5 * 60,
      transformResponse: (response: Schema<'NotificationUnreadCountEnvelopeResponse'>) => response.data,
    }),
    markNotificationRead: builder.mutation<void, { id: string; data: { read: boolean } }>({
      query: ({ id, data }) => ({
        url: `/notifications/${id}/read`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['Notifications', 'NotificationCount'],
    }),
    getNotificationSettings: builder.query<NotificationPreferences, void>({
      query: () => '/notifications/settings',
      providesTags: ['NotificationSettings'],
      transformResponse: (response: Schema<'NotificationSettingsGetResponse'>) => response.data,
    }),
    updateNotificationSettings: builder.mutation<NotificationPreferences, Schema<'NotificationUpdateSettingsBody'>>({
      query: (payload) => ({
        url: '/notifications/settings',
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: ['NotificationSettings'],
      transformResponse: (response: Schema<'NotificationSettingsUpdateResponse'>) => response.data,
    }),
    getOrganizationAuditLog: builder.query<AuditLogResponse, AuditLogQueryParams>({
      query: ({ page = 1, limit = 20, type = 'all' } = {}) =>
        `/notifications/audit-log?page=${page}&limit=${limit}&type=${type}`,
      // Use specific tags per filter type to prevent cache collision between filters
      providesTags: (_result, _error, arg) => [
        { type: 'AuditLog' as const, id: arg?.type || 'all' },
        'AuditLog',
      ],
      // Always refetch when filter changes to ensure fresh data
      keepUnusedDataFor: 30,
      transformResponse: (response: Schema<'NotificationAuditLogEnvelopeResponse'>) => response.data,
    }),
  })
});

export const {
  useGetNotificationFeedQuery,
  useGetNotificationUnreadCountQuery,
  useMarkNotificationReadMutation,
  useGetNotificationSettingsQuery,
  useUpdateNotificationSettingsMutation,
  useGetOrganizationAuditLogQuery
} = notificationsApi;